import { useMemo } from 'react';
import { Box, Button, Grid2, Typography } from '@mui/material';
import { useCheckout } from 'core/contexts/checkout';
import { useUser } from 'core/contexts/user';
import { useTranslation } from '../../../../core/contexts/localization';
import ExperienceImageDefault from '../../../../assets/images/experience_placeholder.png';

const OrderSummary = () => {
    const { t: translate } = useTranslation();

    const { bookingItems, prices, onStripeCreateOrder, /* onEpaycoCreateOrder, */ onBookCreateOrder, cancelStatusPaymentLink } = useCheckout();
    const { userData, canSubmitForm } = useUser();
    const { totalPriceCOP, totalPriceNowCOP, totalPriceUSD, totalPriceNowUSD } = prices;
    const firstCurrencyItem = useMemo(() => bookingItems?.[0]?.currency ?? 'COP', [bookingItems]);

    const handleClick = async () => {
        let paymentMethod = async () => {};
        if (totalPriceNowCOP <= 0 && totalPriceNowUSD <= 0) {
            paymentMethod = onBookCreateOrder;
        } else {
            // NOTE: BAC: Temporary comment this until we are approved by ePayco
            // paymentMethod = userData.country === 'Colombia' ? onEpaycoCreateOrder : onStripeCreateOrder;
            paymentMethod = onStripeCreateOrder;
        }

        await paymentMethod(userData);
    };

    const getTotalPrice = (country, totalCOP, totalUSD) => {
        const totalCOPLabel = 'COP '.concat(totalCOP);
        const totalUSDLabel = 'USD '.concat(totalUSD);
        if (!country) {
            if (firstCurrencyItem === 'USD') return totalUSDLabel;

            return totalCOPLabel;
        }

        switch (country) {
            case 'Colombia':
                return totalCOPLabel;
            default:
                return totalUSDLabel;
        }
    };

    return (
        <Box sx={{ width: { xs: '90%', md: '50vw' }, marginLeft: '1.5em', marginRight: { xs: '1.5em', md: 0 }, marginTop: { xs: '3.5em', md: 0 } }}>
            <Box style={{ fontSize: '2rem', marginBottom: '2rem' }}>
                <strong>{translate('checkout.order_summary')}</strong>
            </Box>
            <Box
                sx={{
                    minHeight: '10vh',
                    maxHeight: '50vh',
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '.5em',
                    marginBottom: '1.5em',
                    gap: '1.2em',
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'gray transparent',
                    '&::-webkit-scrollbar': {
                        width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: 'gray',
                        borderRadius: '6px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                        background: '#ddd',
                    },
                }}>
                {bookingItems.map((bkg, index) => (
                    <Grid2 container key={index} size={12} sx={{ padding: '.5em', boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)', borderRadius: '.25em' }}>
                        <Box
                            sx={{
                                borderRadius: '.5em',
                                minWidth: '5em',
                                width: '20%',
                                maxWidth: '18em',
                                minHeight: '3em',
                                maxHeight: '12em',
                                backgroundImage: `url("${bkg.experienceImage ? process.env.REACT_APP_API_URL.concat(bkg?.experienceImage) : ExperienceImageDefault}")`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                            }}
                        />
                        <Grid2 container flexDirection="column" ml={2}>
                            <Typography variant="h2" sx={{ marginBottom: '.5rem', fontSize: { xs: '1.2em', md: '1.5em' }, fontWeight: 600 }}>
                                {bkg?.experienceName}
                            </Typography>
                            <Grid2 container flexDirection="column" spacing={1}>
                                <Typography variant="body1" sx={{ fontWeight: 600, fontSize: { xs: '.8em', md: '1em' } }}>
                                    {`${translate('checkout.pay_now')}: ${bkg?.currency} ${bkg?.payNow}`}
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600, fontSize: { xs: '.8em', md: '1em' } }}>
                                    {`${translate('checkout.pay_total')}: ${bkg?.currency} ${bkg?.price}`}
                                </Typography>
                            </Grid2>
                        </Grid2>
                    </Grid2>
                ))}
            </Box>
            <Grid2 container flexDirection="column" spacing={1} mb={2}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '1em' }}>
                    {`${translate('checkout.total_pay_now')}: ${getTotalPrice(userData.country, totalPriceNowCOP, totalPriceNowUSD)}`}
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '1em' }}>
                    {`${translate('checkout.total_pay_total')}: ${getTotalPrice(userData.country, totalPriceCOP, totalPriceUSD)}`}
                </Typography>
            </Grid2>
            <Grid2 container spacing={3}>
                <Button
                    type="button"
                    style={{
                        minWidth: '10em',
                        color: 'grey',
                    }}
                    onClick={cancelStatusPaymentLink}>
                    <span style={{ textDecoration: 'underline' }}>{translate('checkout.cancel_order')}</span>
                </Button>
                <Button type="button" size="large" variant="contained" color="primary" disabled={!canSubmitForm(true)} onClick={handleClick} sx={{ backgroundColor: '#2a81e8', minWidth: '10em' }}>
                    {translate('checkout.book_order')}
                </Button>
            </Grid2>
        </Box>
    );
};

export default OrderSummary;
